import React, { useEffect, useRef, useState } from 'react';
import { Search, X, UserPlus, Check } from 'lucide-react';
import { apiGet } from '../lib/api';

/**
 * UserEmailCombobox
 * ------------------
 * Search-as-you-type input for the Add Employee form's Email field.
 * Behavior:
 *   - User types → filters existing users (from /api/user-management/users) by name OR email
 *   - Matching users appear as suggestions; selecting one fills the parent form with
 *     {email, name, department, role, user_id} so the new employee will LINK to that user
 *   - If no match, an inline "+ Add new user with email '<typed>'" option appears at the
 *     bottom — choosing it just sets the email (parent form auto-creates the user on save)
 *   - Existing users that are ALREADY linked to another employee are flagged with a
 *     warning chip so HR can decide consciously before re-linking
 *
 * Props:
 *   value      string — current email
 *   onChange   (email, matchedUser|null) — fires on every change. matchedUser carries
 *              {user_id, email, name, department, role, already_linked_to_emp_id} or null
 *   editingId  string — if set, the combobox falls back to a plain readonly input (no lookup on edit)
 */
export default function UserEmailCombobox({ value, onChange, editingId, testid = 'emp-form-email' }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  // Fetch all users once on mount
  useEffect(() => {
    if (editingId) return;
    let abort = false;
    setLoading(true);
    apiGet('/user-management/users')
      .then(d => {
        if (abort) return;
        const list = Array.isArray(d?.users) ? d.users : (Array.isArray(d) ? d : []);
        setUsers(list);
      })
      .catch(() => {})
      .finally(() => { if (!abort) setLoading(false); });
    return () => { abort = true; };
  }, [editingId]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!containerRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = (query || '').trim().toLowerCase();
  const matches = q
    ? users.filter(u =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.name || '').toLowerCase().includes(q)
      ).slice(0, 8)
    : [];
  const exactMatch = users.find(u => (u.email || '').toLowerCase() === q);

  const pick = (u) => {
    setSelectedUser(u);
    setQuery(u.email);
    setOpen(false);
    onChange(u.email, {
      user_id: u.user_id,
      email: u.email,
      name: u.name,
      department: u.department,
      role: u.role,
      already_linked_to_emp_id: u.employee_id,
    });
  };

  const useTypedEmail = () => {
    setSelectedUser(null);
    setOpen(false);
    onChange(query, null);
  };

  const clearSelection = () => {
    setSelectedUser(null);
    setQuery('');
    onChange('', null);
  };

  // ── Edit mode: editable email field with sync warning
  if (editingId) {
    return (
      <div className="sm:col-span-2">
        <label className="text-[11px] text-slate-600 mb-1 block">Email *</label>
        <input
          type="email"
          value={value || ''}
          onChange={(e) => onChange(e.target.value, null)}
          required
          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
          data-testid={testid}
        />
        <div className="text-[10px] text-amber-700 mt-0.5">
          ⓘ Changing email will also update the linked user login account.
          The user will need to use the new email to log in next time.
        </div>
      </div>
    );
  }

  return (
    <div className="sm:col-span-2 relative" ref={containerRef}>
      <label className="text-[11px] text-slate-600 mb-1 block">
        Email * <span className="text-slate-400">(Search existing users or type a new email)</span>
      </label>
      <div className={`relative w-full border rounded-sm flex items-center ${selectedUser ? 'border-indigo-300 bg-indigo-50/30' : 'border-slate-300'}`}>
        <Search size={14} className="absolute left-2.5 text-slate-400" />
        <input
          type="email"
          value={query}
          onChange={(e) => {
            const v = e.target.value;
            setQuery(v);
            setSelectedUser(null);
            onChange(v, null);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Type to search by name or email…"
          className="w-full pl-8 pr-8 py-2 text-sm bg-transparent focus:outline-none"
          data-testid={testid}
          required
          autoComplete="off"
        />
        {query && (
          <button type="button" onClick={clearSelection} className="absolute right-2 text-slate-400 hover:text-slate-700" tabIndex={-1} data-testid={`${testid}-clear`}>
            <X size={13} />
          </button>
        )}
      </div>

      {selectedUser && (
        <div className="mt-1.5 p-2 bg-indigo-50 border border-indigo-200 rounded-sm flex items-start gap-2 text-xs" data-testid="emp-form-link-preview">
          <Check size={14} className="text-indigo-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-indigo-900">
              Linked: {selectedUser.name || selectedUser.email}{' '}
              <span className="font-mono text-[10px] text-indigo-700">({selectedUser.role}{selectedUser.department ? ` · ${selectedUser.department}` : ''})</span>
            </div>
            <div className="text-[10px] text-indigo-700/80">
              {selectedUser.employee_id
                ? `⚠️ Already linked to employee "${selectedUser.employee_id}" — saving will re-link to this new employee.`
                : 'New employee will be linked to this existing user (role / password / module access preserved).'}
            </div>
          </div>
        </div>
      )}

      {open && (matches.length > 0 || (q && !exactMatch)) && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-64 overflow-y-auto" data-testid="emp-email-dropdown">
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Loading users…</div>}
          {matches.map((u) => (
            <button
              key={u.user_id || u.email}
              type="button"
              onClick={() => pick(u)}
              data-testid={`emp-email-option-${u.email}`}
              className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-slate-100 last:border-b-0 flex items-start gap-2"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{u.name || '—'}</div>
                <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
              </div>
              <div className="flex flex-col items-end shrink-0 gap-0.5">
                <span className="text-[9px] uppercase tracking-widest font-display font-bold text-[#3b4872]">{u.role}</span>
                {u.department && <span className="text-[10px] text-slate-500">{u.department}</span>}
                {u.employee_id && <span className="text-[9px] text-amber-700 bg-amber-50 px-1.5 rounded-sm">linked: {u.employee_id}</span>}
              </div>
            </button>
          ))}
          {q && !exactMatch && (
            <button
              type="button"
              onClick={useTypedEmail}
              data-testid="emp-email-add-new"
              className="w-full text-left px-3 py-2 bg-emerald-50 hover:bg-emerald-100 border-t border-slate-200 flex items-center gap-2"
            >
              <UserPlus size={14} className="text-emerald-700" />
              <div className="text-xs text-emerald-900">
                <span className="font-semibold">+ Add new user</span> with email <span className="font-mono">{query}</span>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
